import type {
    Subscription,
    SubscriptionPlan,
    SubscriptionStatus,
} from '@thinkalpha/access-ws-client/contracts/subscription.js';
import type { ProTraderStatus } from '@thinkalpha/common/schemas/auth.js';
import { minBy } from 'es-toolkit';
import type { z } from 'zod';

type Plan = z.infer<typeof SubscriptionPlan>;
type SubscriptionType = z.infer<typeof Subscription>;

const PRO_TRADER_STATUS_TO_IS_PROFESSIONAL_TRADER = {
    pro: true,
    'non-pro': false,
    unspecified: false,
} satisfies Record<ProTraderStatus, boolean>;

export const ACTIVE_SUBSCRIPTION_STATUSES: ReadonlySet<SubscriptionType['status']> = new Set([
    'active',
    'trialing',
    'active_canceling',
    'active_plan_change_scheduled',
    'pending_activation',
]);

export const PLAN_CHANGEABLE_SUBSCRIPTION_STATUSES: ReadonlySet<SubscriptionType['status']> = new Set([
    'active',
    'trialing',
    'active_plan_change_scheduled',
]);

export function statusToIsProfessionalTrader(status: ProTraderStatus): boolean {
    return PRO_TRADER_STATUS_TO_IS_PROFESSIONAL_TRADER[status];
}

export function isPlanAvailableForTraderStatus(plan: Plan, isProfessionalTrader: boolean): boolean {
    return isProfessionalTrader ? plan.isForProfessionalTraders : plan.isForNonProfessionalTraders;
}

export function isPlanSelectableForTraderStatus(plan: Plan, isProfessionalTrader: boolean): boolean {
    return (
        plan.active === true && plan.pricing.length > 0 && isPlanAvailableForTraderStatus(plan, isProfessionalTrader)
    );
}

export function isActiveSubscriptionStatus(status: SubscriptionType['status']): boolean {
    return ACTIVE_SUBSCRIPTION_STATUSES.has(status);
}

export function isPlanChangeableSubscriptionStatus(status: SubscriptionType['status']): boolean {
    return PLAN_CHANGEABLE_SUBSCRIPTION_STATUSES.has(status);
}

export function isMainSubscription(subscription: { readonly plan: Pick<Plan, 'isAddOn'> }): boolean {
    return subscription.plan.isAddOn !== true;
}

/**
 * Ranks statuses for summarizing a user's main subscriptions as one: entitled first, then in
 * progress or at risk, then ended. A user who abandoned checkout and later subscribed holds both
 * an `aborted` and an `active` subscription, and summarizes as `active`.
 */
const SUMMARY_STATUS_RANK = {
    active: 0,
    active_plan_change_scheduled: 1,
    active_canceling: 2,
    trialing: 3,
    pending_activation: 4,
    past_due: 5,
    paused: 6,
    pending_payment_method: 7,
    canceled: 8,
    aborted: 9,
} satisfies Record<SubscriptionStatus, number>;

/**
 * The single status that best describes where a user stands with their main (non-add-on)
 * subscriptions, or `none` when they have never started one.
 */
export function summarizeSubscriptionStatus(
    subscriptions: ReadonlyArray<{ readonly plan: Pick<Plan, 'isAddOn'>; readonly status: SubscriptionStatus }>,
): SubscriptionStatus | 'none' {
    return (
        minBy(subscriptions.filter(isMainSubscription), (subscription) => SUMMARY_STATUS_RANK[subscription.status])
            ?.status ?? 'none'
    );
}

export function getPlanChangeSubscription(
    subscriptions: ReadonlyArray<SubscriptionType>,
): SubscriptionType | undefined {
    return subscriptions.find((subscription) => {
        return isMainSubscription(subscription) && isPlanChangeableSubscriptionStatus(subscription.status);
    });
}
